'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Sparkles, Send, Bot, User, TrendingUp, MapPin, Bed, Maximize, ArrowRight, Loader2 } from 'lucide-react';

const EXAMPLE_QUERIES = [
  'İstanbul Kadıköy\'de 3+1, 10 milyon TL altında, otoparklı daire bul.',
  'Muğla\'da denize yakın, havuzlu müstakil villa istiyorum.',
  'Antalya\'da yatırım için kira getirisi yüksek daireler göster.',
  'Bodrum\'da deniz manzaralı 2+1 daire.',
  'İstanbul\'da metroya yakın, yeni binada 2+1 daire.',
];

const MOCK_AI_RESPONSE = {
  text: 'Kadıköy\'de bütçenize uygun 3+1, otoparklı 14 ilan buldum. Ortalama fiyat ₺9.2M, m² başına ₺61.300. En iyi yatırım skoru olan 3 ilan öne çıktı:',
  properties: [
    { id: 'ai-prop-001', title: 'Moda\'da 3+1 Deniz Manzaralı', price: '₺9.750.000', area: 148, score: 82, change: 'Fiyat Düştü' },
    { id: 'ai-prop-002', title: 'Fenerbahçe\'de 3+1 Yeni Bina', price: '₺8.900.000', area: 135, score: 79, change: 'Yeni İlan' },
    { id: 'ai-prop-003', title: 'Bağdat Cad. 3+1 Rezidans', price: '₺10.200.000', area: 162, score: 76, change: 'Öne Çıkan' },
  ],
};

export default function AISearchSection() {
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [conversation, setConversation] = useState<Array<{ role: 'user' | 'ai'; content: string }>>([]);

  const handleSearch = (query: string) => {
    if (!query.trim()) return;
    setIsLoading(true);
    setShowResult(false);
    setConversation([{ role: 'user', content: query }]);

    // Backend integration point: POST /api/ai/search { query }
    setTimeout(() => {
      setIsLoading(false);
      setShowResult(true);
      setConversation(prev => [...prev, { role: 'ai', content: MOCK_AI_RESPONSE.text }]);
    }, 1800);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-foreground via-slate-900 to-primary overflow-hidden relative">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `radial-gradient(circle at 20% 50%, #1B4FFF 0%, transparent 50%), radial-gradient(circle at 80% 20%, #FF6B2B 0%, transparent 40%)`
        }}
      />

      <div className="relative z-10 max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Description */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90 text-sm font-semibold mb-6">
              <Sparkles size={14} className="text-accent" />
              AI Destekli Arama
            </div>
            <h2 className="text-hero-lg font-extrabold text-white mb-4">
              Doğal dille<br />
              <span className="text-accent">ev ara.</span>
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8">
              Aklınızdaki evi Türkçe olarak tarif edin. AI asistanımız filtreleri otomatik oluşturur ve en uygun ilanları bulur.
            </p>

            <div className="space-y-3 mb-8">
              {[
                { icon: <MapPin size={16} />, text: 'Konum, fiyat, oda sayısı otomatik algılar' },
                { icon: <TrendingUp size={16} />, text: 'Yatırım potansiyelini analiz eder' },
                { icon: <Sparkles size={16} />, text: 'Benzer ilanları akıllıca önerir' },
              ].map((item, i) => (
                <div key={`ai-feature-${i}`} className="flex items-center gap-3 text-white/80">
                  <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center text-accent flex-shrink-0">
                    {item.icon}
                  </div>
                  <span className="text-sm font-medium">{item.text}</span>
                </div>
              ))}
            </div>

            <Link href="/property-search?ai=true" className="btn-accent inline-flex items-center gap-2">
              AI Asistanı Dene
              <ArrowRight size={16} />
            </Link>
          </div>

          {/* Right: Chat Interface */}
          <div className="glass-card rounded-2xl overflow-hidden shadow-2xl">
            {/* Chat Header */}
            <div className="flex items-center gap-3 p-4 border-b border-white/20">
              <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
                <Bot size={18} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-foreground">Evora AI Asistan</p>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-success"></div>
                  <p className="text-xs text-muted-foreground">Aktif · Yanıt süresi &lt;2 sn</p>
                </div>
              </div>
            </div>

            {/* Chat Body */}
            <div className="p-4 min-h-[240px] max-h-80 overflow-y-auto space-y-3">
              {conversation.length === 0 && !isLoading && (
                <div className="text-center py-6">
                  <div className="w-12 h-12 rounded-2xl bg-primary-light flex items-center justify-center mx-auto mb-3">
                    <Sparkles size={24} className="text-primary" />
                  </div>
                  <p className="text-sm text-muted-foreground font-medium mb-4">Nasıl bir ev arıyorsunuz?</p>
                  <div className="space-y-2">
                    {EXAMPLE_QUERIES.slice(0, 3).map((q, i) => (
                      <button
                        key={`example-q-${i}`}
                        onClick={() => { setInputValue(q); handleSearch(q); }}
                        className="w-full text-left text-xs text-primary font-medium px-3 py-2 rounded-lg bg-primary-light hover:bg-primary hover:text-white transition-all duration-150"
                      >
                        &ldquo;{q.substring(0, 55)}...&rdquo;
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {conversation.map((msg, i) => (
                <div key={`msg-${i}`} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'ai' && (
                    <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Bot size={14} className="text-white" />
                    </div>
                  )}
                  <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${msg.role === 'user' ? 'bg-primary text-white rounded-tr-sm' : 'bg-muted text-foreground rounded-tl-sm'}`}>
                    {msg.content}
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-7 h-7 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0 mt-0.5">
                      <User size={14} className="text-muted-foreground" />
                    </div>
                  )}
                </div>
              ))}

              {isLoading && (
                <div className="flex gap-2 justify-start">
                  <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
                    <Bot size={14} className="text-white" />
                  </div>
                  <div className="bg-muted rounded-xl rounded-tl-sm px-4 py-3 flex items-center gap-2">
                    <Loader2 size={14} className="text-primary animate-spin" />
                    <span className="text-sm text-muted-foreground">Aranıyor...</span>
                  </div>
                </div>
              )}

              {showResult && (
                <div className="space-y-2 mt-2">
                  {MOCK_AI_RESPONSE.properties.map((prop) => (
                    <Link key={prop.id} href="/property-detail" className="block">
                      <div className="flex items-center gap-3 p-2.5 rounded-xl bg-white border border-border hover:border-primary hover:bg-primary-light transition-all duration-150 group">
                        <div className="w-8 h-8 rounded-lg bg-primary-light flex items-center justify-center flex-shrink-0">
                          <Bed size={14} className="text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-foreground line-clamp-1">{prop.title}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs font-bold text-primary tabular-nums">{prop.price}</span>
                            <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Maximize size={9} />{prop.area}m²</span>
                          </div>
                        </div>
                        <div className={`text-xs font-bold px-2 py-0.5 rounded-md ${prop.score >= 80 ? 'bg-success-light text-success' : 'bg-warning-light text-warning'}`}>
                          {prop.score}/100
                        </div>
                      </div>
                    </Link>
                  ))}
                  <Link href="/property-search?q=kadikoy-3-1-otopark" className="text-xs text-primary font-semibold hover:underline flex items-center gap-1 mt-2">
                    Tüm 14 ilanı gör <ArrowRight size={12} />
                  </Link>
                </div>
              )}
            </div>

            {/* Chat Input */}
            <div className="p-3 border-t border-border">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch(inputValue)}
                  placeholder="Aradığınız evi tarif edin..."
                  className="flex-1 px-3 py-2.5 rounded-xl border border-border text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                />
                <button
                  onClick={() => handleSearch(inputValue)}
                  disabled={isLoading || !inputValue.trim()}
                  className="btn-primary px-3 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}